import { AfterViewInit, Component, ElementRef, Input, NgZone, OnChanges, OnDestroy } from '@angular/core';
import { DwComponent, DwComponentType } from '@devwareapps/devware-cap';
import { DocumentViewerTraceService } from '../../services/document-viewer-trace.service';

@DwComponent({
  key: 'document-pdf-viewer',
  name: 'Document PDF Viewer',
  componentType: DwComponentType.pageComponent,
  isGlobal: true,
  //parentItemName: [ AppMetaDataItemNames.ProcessorConfig ]
})
@Component({
  selector: 'app-document-embedded-viewer',
  template: `
    <div class="embedded-viewer" [attr.aria-busy]="isLoading">
      <dw-html-view [ngModel]="safeEmbeddedHtml"></dw-html-view>
      <div class="model-skeleton" *ngIf="isLoading" role="status">
        <div class="model-skeleton__canvas" aria-hidden="true">
          <div class="model-skeleton__shape skeleton"></div>
          <div class="model-skeleton__toolbar">
            <span class="skeleton"></span><span class="skeleton"></span><span class="skeleton"></span>
          </div>
        </div>
        <span class="model-skeleton__label">Loading interactive model…</span>
      </div>
    </div>
  `,
  styleUrls: ['./document-embedded-viewer.component.scss']
})
export class DocumentEmbeddedViewerComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() embeddedHtml: string = '';

  safeEmbeddedHtml: string = this.embeddedHtml;
  isLoading = true;
  private readonly traceId: number;
  private readonly frames = new WeakMap<HTMLIFrameElement, number>();
  private frameCount = 0;
  private loadCount = 0;

  constructor(private readonly host: ElementRef<HTMLElement>, private readonly zone: NgZone,
    private readonly viewerTrace: DocumentViewerTraceService) {
    this.traceId = viewerTrace.nextViewerId();
    this.trace('embedded.created');
  }

  ngOnChanges(): void {
    const html = this.disableSketchfabAutospin(this.embeddedHtml);
    if (html !== this.safeEmbeddedHtml) {
      this.safeEmbeddedHtml = html;
      this.isLoading = !html || /<iframe\b/i.test(html);
      this.trace('embedded.markup-changed');
    }
  }

  ngAfterViewInit(): void {
    // iframe load does not bubble. Capture it on the host because dw-html-view
    // creates the iframe from CMS markup rather than an Angular template.
    this.host.nativeElement.addEventListener('load', this.onFrameLoad, true);
  }

  ngOnDestroy(): void {
    this.trace('embedded.destroyed');
    this.host.nativeElement.removeEventListener('load', this.onFrameLoad, true);
  }

  private readonly onFrameLoad = (event: Event): void => {
    const frame = event.target as HTMLIFrameElement;
    if (frame?.tagName !== 'IFRAME' || !this.host.nativeElement.contains(frame)) {
      return;
    }
    if (this.viewerTrace.enabled) {
      if (!this.frames.has(frame)) {
        this.frames.set(frame, ++this.frameCount);
      }
      this.viewerTrace.record('embedded.frame-loaded', {
        embeddedId: this.traceId, frameId: this.frames.get(frame), loadCount: ++this.loadCount
      });
    }
    // This marks the handoff to the provider's own loader, not model readiness.
    this.zone.run(() => this.isLoading = false);
  };

  private trace(event: string): void {
    this.viewerTrace.record(event, { embeddedId: this.traceId });
  }

  // Sketchfab embeds spin the model when the iframe URL has `autospin=N`
  // with N > 0. We force it to 0 on the way in so existing CMS records
  // don't all have to be edited by hand.
  private disableSketchfabAutospin(html: string): string {
    if (!html) {
      return html;
    }

    let result = html.replace(/([?&])autospin=[^&"'\s>]*/gi, '$1autospin=0');

    // If a Sketchfab embed URL doesn't already specify autospin, append it
    // so the default platform behavior can't reintroduce a spin.
    result = result.replace(
      /(src=["'][^"']*sketchfab\.com\/models\/[^"']+?\/embed(?:\?[^"']*)?)(["'])/gi,
      (match, srcUpToQuote: string, quote: string) => {
        if (/[?&]autospin=/i.test(srcUpToQuote)) {
          return match;
        }
        const joiner = srcUpToQuote.includes('?') ? '&' : '?';
        return `${srcUpToQuote}${joiner}autospin=0${quote}`;
      }
    );

    return result;
  }
}
